import { useContext, useState } from "react";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import {
  User,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Calendar,
  Send,
  EyeOff,
} from "lucide-react";
import { cn } from "@lib/utils/helpers";
import { isApprovedStatus } from "@lib/utils/applicationHeaderHelpers";
import { useAppDispatch } from "@/app/hooks";
import { endAdminImpersonation } from "@/lib/auth/endAdminImpersonation";
import { formatApiError, showWarning } from "@/lib/utils/notifications";
import { SubmitForProcessingContext } from "@/features/applicant/context/SubmitForProcessingContext";

interface ApplicationHeaderProps {
  applicantId?: string;
  applicationId?: string;
  status?: string;
  rentAdded?: boolean;
  balanceDue?: number;
  paymentDueDate?: string;
  applicantStatus?: string;
  nextStep?: string;
  showSubmitForProcessing?: boolean;
  isApplicationLocked?: boolean;
  coApplicants?: string;
}

const statusConfig: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bgColor: string;
  }
> = {
  [APPLICANT_CONTENT.application.status.creditReportPending]: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  PENDING: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  Pending: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  APPROVED: {
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-50 border-green-200",
  },
  Approved: {
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-50 border-green-200",
  },
  "Screening Approved": {
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-50 border-green-200",
  },
  INCOMPLETE: {
    icon: AlertCircle,
    color: "text-orange-600",
    bgColor: "bg-orange-50 border-orange-200",
  },
  Incomplete: {
    icon: AlertCircle,
    color: "text-orange-600",
    bgColor: "bg-orange-50 border-orange-200",
  },
  Complete: {
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-50 border-green-200",
  },
  Rejected: {
    icon: XCircle,
    color: "text-red-600",
    bgColor: "bg-red-50 border-red-200",
  },
  DENIED: {
    icon: XCircle,
    color: "text-red-600",
    bgColor: "bg-red-50 border-red-200",
  },
  Denied: {
    icon: XCircle,
    color: "text-red-600",
    bgColor: "bg-red-50 border-red-200",
  },
  EXPIRED: {
    icon: XCircle,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  Expired: {
    icon: XCircle,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  AWAITING: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  Awaiting: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-50 border-amber-200",
  },
  VOID: {
    icon: AlertCircle,
    color: "text-gray-600",
    bgColor: "bg-gray-50 border-gray-200",
  },
  Void: {
    icon: AlertCircle,
    color: "text-gray-600",
    bgColor: "bg-gray-50 border-gray-200",
  },
};

export function ApplicationHeader({
  applicantId = "-",
  applicationId = "-",
  status = "-",
  rentAdded = false,
  balanceDue,
  paymentDueDate,
  applicantStatus,
  nextStep,
  showSubmitForProcessing = false,
  isApplicationLocked = false,
  coApplicants,
}: ApplicationHeaderProps) {
  const dispatch = useAppDispatch();
  const submitContext = useContext(SubmitForProcessingContext);
  const statusInfo = statusConfig[status] || {
    icon: Clock,
    color: "text-gray-600",
    bgColor: "bg-gray-50 border-gray-200",
  };
  const StatusIcon = statusInfo.icon;

  // Impersonation Logic
  const isAdminImpersonating =
    !!localStorage.getItem("admin_token") ||
    localStorage.getItem("impersonate") === "true";
  const [stopImpersonateBusy, setStopImpersonateBusy] = useState(false);

  const handleStopImpersonate = async () => {
    if (stopImpersonateBusy) return;
    setStopImpersonateBusy(true);
    try {
      const originalApplicantId = localStorage.getItem("impersonated_applicant_id");
      const redirect = await endAdminImpersonation({
        dispatch,
        applicantFallback: {
          originalApplicantId,
          applicationsListPath: "/application",
        },
      });
      window.location.href = redirect;
    } catch (e) {
      showWarning(formatApiError(e));
      setStopImpersonateBusy(false);
    }
  };

  // Format applicant ID as 8-digit number
  const formattedApplicantId =
    applicantId && applicantId !== "-"
      ? String(applicantId).padStart(8, "0")
      : applicantId;

  // Format application ID as 6-digit number
  const formattedApplicationId =
    applicationId && applicationId !== "-"
      ? String(applicationId).padStart(6, "0")
      : applicationId;

  // Determine contract payment status display
  const showContractPaymentConfirmed =
    rentAdded && (!balanceDue || Number(balanceDue) === 0);

  // Show Payment Due Date when:
  // 1. Contract not paid (rent_added is false)
  // 2. Payment due date exists
  // 3. Application is approved (either applicant_status is APPROVED or status indicates approval)
  const showPaymentDueDate =
    !rentAdded && paymentDueDate && isApprovedStatus(status, applicantStatus);

  // Format payment due date
  const formattedPaymentDueDate = paymentDueDate
    ? new Date(paymentDueDate).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
    : "";

  return (
    <header className="border-b border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-6 md:px-8 mb-0">
      <div className="mx-auto flex max-w-full flex-col justify-between gap-4 sm:flex-row sm:items-center sm:gap-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {APPLICANT_CONTENT.application.title}
          </h1>
          <p className="mt-0.5 text-xs text-gray-600">
            {APPLICANT_CONTENT.application.subtitle}
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4 md:gap-6">
          {isAdminImpersonating && (
            <button
              type="button"
              onClick={() => void handleStopImpersonate()}
              disabled={stopImpersonateBusy}
              className="flex flex-shrink-0 items-center gap-2 rounded-md bg-[rgb(21_151_255)] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:opacity-60"
            >
              <EyeOff className="h-4 w-4" />
              <span>
                {stopImpersonateBusy ? "Stopping…" : "Stop Impersonate"}
              </span>
            </button>
          )}

          <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 sm:gap-3 md:gap-4">
            <div className="flex min-w-0 flex-shrink-0 items-center gap-1.5 sm:gap-2">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-blue-100">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.applicantId}
                </p>
                <p className="truncate text-xs font-bold text-gray-900 sm:text-sm">
                  {formattedApplicantId}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 flex-shrink-0 items-center gap-1.5 sm:gap-2">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-indigo-100">
                <FileText className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.applicationId}
                </p>
                <p className="truncate text-xs font-bold text-gray-900 sm:text-sm">
                  {formattedApplicationId}
                </p>
              </div>
            </div>

            <div className="group relative flex min-w-0 flex-shrink-0 cursor-help items-center gap-1.5 sm:gap-2">
              <div
                className={cn(
                  "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md",
                  statusInfo.bgColor,
                )}
              >
                <StatusIcon className={cn("h-4 w-4", statusInfo.color)} />
              </div>
              <div className="min-w-0 max-w-[10rem] text-left sm:max-w-[12rem]">
                <p className="truncate text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.applicantStatus}
                </p>
                <p className={cn("truncate text-xs font-bold sm:text-sm", statusInfo.color)}>
                  {status}
                </p>
              </div>

              {nextStep && (
                <div className="invisible absolute top-full right-0 z-50 mt-2 max-w-[calc(100vw-2rem)] w-56 rounded bg-gray-800 p-3 text-left text-xs text-white opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100 sm:w-64">
                  {nextStep.trim().toLowerCase() === "cancelled" ? (
                    <div className="font-bold">Cancelled</div>
                  ) : (
                    <>
                      <div className="mb-1 font-bold">What&apos;s Next:</div>
                      {nextStep}
                    </>
                  )}
                  <div className="absolute bottom-full right-6 border-8 border-transparent border-b-gray-800" />
                </div>
              )}
            </div>
          </div>

          {coApplicants && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <div className="max-w-[12rem] text-left sm:max-w-[16rem]">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.coApplicants}
                </p>
                <p className="truncate text-sm font-bold text-gray-900">
                  {coApplicants}
                </p>
              </div>
            </div>
          )}

          {showContractPaymentConfirmed && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-green-100">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.contractPayment}
                </p>
                <p className="text-sm font-bold text-green-600">
                  {APPLICANT_CONTENT.application.confirmed}
                </p>
              </div>
            </div>
          )}

          {showPaymentDueDate && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-amber-100">
                <Calendar className="h-4 w-4 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  {APPLICANT_CONTENT.application.paymentDueDate}
                </p>
                <p className="text-sm font-bold text-amber-600">
                  {formattedPaymentDueDate}
                </p>
              </div>
            </div>
          )}

          {showSubmitForProcessing && (
            <div className={isApplicationLocked ? "pointer-events-none" : ""}>
              <div className="flex flex-shrink-0 items-center">
                <button
                  type="button"
                  onClick={isApplicationLocked ? undefined : submitContext?.openConfirmDialog}
                  className="inline-flex items-center gap-2 rounded-md bg-[rgb(21_151_255)] px-4 py-2 text-sm font-medium text-white hover:bg-[#0d7acc]"
                >
                  <Send className="h-4 w-4 flex-shrink-0" />
                  <span>Submit for Processing</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
